/**
 * Recover from stale lazy-chunk loads after a deploy.
 *
 * When a new build ships, the hashed chunk filenames change and the old ones
 * 404. A tab that's been open across the deploy still references the old names,
 * so a dynamic `import()` (a lazy route, a code-split panel) fetches a missing
 * file — the server returns index.html, the browser rejects it as a module
 * ("MIME type text/html"), and the navigation throws. Vite raises a
 * `vite:preloadError` for exactly this case.
 *
 * The fix is simply to reload: a fresh document pulls the new index.html and the
 * new chunk names. We guard with a short cooldown so a genuinely-broken chunk
 * (not just a stale one) can't trap the user in a reload loop — after one
 * reload attempt within the window, we let the error surface to the router's
 * error boundary instead.
 */
const COOLDOWN_MS = 10_000
const KEY = 'chunk-reload-at'

if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    let last = 0
    try {
      last = Number(window.sessionStorage.getItem(KEY) ?? 0)
    } catch {
      // sessionStorage can throw in privacy modes — fall through to reload once.
    }
    const now = Date.now()
    if (now - last < COOLDOWN_MS) return // already tried recently — let it surface
    // Stop Vite's default (re-throwing the error) so the reload is the outcome.
    event.preventDefault()
    try {
      window.sessionStorage.setItem(KEY, String(now))
    } catch {
      /* ignore */
    }
    window.location.reload()
  })
}
